import { Component, computed, inject, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogActionsComponent, DialogContentBase, DialogRef } from '@progress/kendo-angular-dialog';
import { ButtonComponent } from '@progress/kendo-angular-buttons';
import {
  DropDownListComponent,
  ItemTemplateDirective,
  ValueTemplateDirective,
} from '@progress/kendo-angular-dropdowns';
import { FormFieldComponent, TextAreaComponent, TextBoxComponent } from '@progress/kendo-angular-inputs';
import { LabelComponent } from '@progress/kendo-angular-label';
import { NotificationService } from '@progress/kendo-angular-notification';
import { environment } from '@environments';
import { ApplicationUserService, CapitalizePipe, errorToast, successSaveToast } from 'shared';

/** The Royal Crown mailboxes a client can ask a question to (`division` of `POST /crown_desk/questions`). */
export const QUESTION_DIVISIONS = [
  'orders',
  'invoices',
  'service',
  'returns',
  'backorders',
  'accounting',
  'complaints',
  'marketing',
  'it',
  'buying',
  'pims',
  'general',
] as const;
export type QuestionDivision = (typeof QUESTION_DIVISIONS)[number];

/** The api limits the message to 5000 characters. */
export const QUESTION_MAX_LENGTH = 5000;

/**
 * "Ask a question" (old "Stel een vraag"): mailed to the chosen division with the employee in CC and as reply-to, so
 * the answer comes by e-mail. Nothing is stored.
 */
@Component({
  selector: 'rc-ask-question-dialog',
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    CapitalizePipe,
    DialogActionsComponent,
    ButtonComponent,
    DropDownListComponent,
    ItemTemplateDirective,
    ValueTemplateDirective,
    FormFieldComponent,
    TextAreaComponent,
    TextBoxComponent,
    LabelComponent,
  ],
  providers: [CapitalizePipe],
  templateUrl: './ask-question-dialog.component.html',
  styleUrl: './ask-question-dialog.component.scss',
})
export class AskQuestionDialogComponent extends DialogContentBase {
  #httpClient = inject(HttpClient);
  #notificationService = inject(NotificationService);
  #translateService = inject(TranslateService);
  #capitalizePipe = inject(CapitalizePipe);
  #user = toSignal(inject(ApplicationUserService).user$);

  /** Shown so the employee knows who asks: the answer goes to this e-mail address. */
  readonly companyName = computed(() => this.#user()?.companyName ?? '');
  readonly email = computed(() => this.#user()?.email ?? '');

  readonly divisions = QUESTION_DIVISIONS.map((value) => ({ value, labelKey: divisionLabelKey(value) }));
  readonly noDivision = { value: null, labelKey: 'SELECT_DIVISION' };
  readonly maxLength = QUESTION_MAX_LENGTH;
  readonly form = new FormGroup({
    division: new FormControl<QuestionDivision | null>(null, Validators.required),
    message: new FormControl('', [Validators.required, Validators.maxLength(QUESTION_MAX_LENGTH)]),
  });
  readonly isSending = signal(false);

  constructor() {
    super(inject(DialogRef));
  }

  send(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    if (this.isSending()) return;
    this.isSending.set(true);
    const { division, message } = this.form.getRawValue();
    this.#httpClient.post<void>(`${environment.apiUrl}/crown_desk/questions`, { division, message }).subscribe({
      next: () => {
        this.isSending.set(false);
        const notification = this.#notificationService.show(successSaveToast);
        if (notification.content) {
          notification.content.instance.custom = this.#capitalizePipe.transform(
            this.#translateService.instant('QUESTION_SENT'),
          );
        }
        this.dialog.close({ sent: true });
      },
      error: (err: HttpErrorResponse) => {
        this.isSending.set(false);
        const notification = this.#notificationService.show(errorToast);
        if (notification.content) notification.content.instance.setCurrentValue(err);
      },
    });
  }

  cancel(): void {
    this.dialog.close();
  }
}

/** e.g. DIVISION_ORDERS; complaints and service reuse the existing division labels. */
export function divisionLabelKey(division: QuestionDivision): string {
  return `DIVISION_${division.toUpperCase()}`;
}
